import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBullseye,
  faMinus,
  faPlus,
  faGamepad,
} from '@fortawesome/free-solid-svg-icons';
import Alert from '../admincomponents/components/Alert';
import Loading from '../admincomponents/components/Loading';
import { generateGeoPlay } from '../../apiservices/axiosservice/subscriberservice';
import { getSubscriber } from '../../apiservices/LocalStorageService';
import GeoPublish from './GeoPublish';
import './GeoGuessSetup.css';

const mapScopes = [
  { id: 'world', emoji: '🌍', label: 'World', sub: 'All continents' },
  { id: 'europe', emoji: '🇪🇺', label: 'Europe', sub: '47 countries' },
  { id: 'asia', emoji: '🌏', label: 'Asia', sub: '48 countries' },
  { id: 'america', emoji: '🌎', label: 'America', sub: 'North & South' },
  { id: 'africa', emoji: '🌍', label: 'Africa', sub: '54 countries' },
  { id: 'oceania', emoji: '🏝️', label: 'Oceania', sub: '14 countries' },
  {
    id: 'capitals',
    emoji: '🏛️',
    label: 'Capital Cities',
    sub: 'World capitals',
  },
  {
    id: 'landmarks',
    emoji: '🗼',
    label: 'Famous Landmarks',
    sub: 'Iconic sites',
  },
];

const difficulties = ['Easy', 'Medium', 'Hard', 'Mixed'];

const GeoGuessSetup: React.FC = () => {
  const navigate = useNavigate();
  const [selectedScope, setSelectedScope] = useState('world');
  const [rounds, setRounds] = useState(5);
  const [timePerRound, setTimePerRound] = useState(1);
  const [difficulty, setDifficulty] = useState('Medium');
  const [gameTitle, setGameTitle] = useState('');
  const [interactiveMode, setInteractiveMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [publishedGameId, setPublishedGameId] = useState<string | null>(null);

  const clamp = (val: number, min: number, max: number) =>
    Math.max(min, Math.min(max, val));

  const handlePublish = async () => {
    if (!gameTitle.trim()) {
      setAlertMsg('Please enter a game title');
      return;
    }
    const subscriber = getSubscriber();
    if (!subscriber?.userId) {
      setAlertMsg('User session not found');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        gameTitle,
        gameOwnerId: subscriber.userId,
        timerEnabled: true,
        gameType: 'geoplay' as const,
        source: 'MAP',
        totalLocations: rounds,
        timeAllowed: timePerRound * 60,
        gameCapacity: 999,
        scheduledGame: !interactiveMode,
        continent: selectedScope.toUpperCase(),
      };
      const res = await generateGeoPlay(payload);

      setPublishedGameId(res.data.gameId);
    } catch (err: any) {
      setAlertMsg(err.message || 'Failed to create game');
    } finally {
      setLoading(false);
    }
  };

  const scopeLabel =
    mapScopes.find((s) => s.id === selectedScope)?.label || 'World';

  if (publishedGameId) {
    return (
      <GeoPublish
        gameId={publishedGameId}
        gameTitle={gameTitle}
        scope={scopeLabel}
        rounds={rounds}
        difficulty={difficulty}
        timePerRound={timePerRound}
      />
    );
  }

  return (
    <div className="geo-setup">
      {/* Header */}
      <div className="geo-setup__header">
        <button
          className="geo-setup__back"
          onClick={() => navigate('/dashboard/games')}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1 className="geo-setup__title">Geo Guess Setup</h1>
      </div>

      <div className="geo-setup__breadcrumb">
        <span className="geo-setup__crumb">Game Type</span>
        <span className="geo-setup__crumb-sep">/</span>
        <span className="geo-setup__crumb geo-setup__crumb--active">
          Geo Guess
        </span>
      </div>

      <div className="geo-setup__body">
        {/* Main content */}
        <div className="geo-setup__main">
          {/* Map Scope */}
          <div className="geo-setup__card">
            <h3 className="geo-setup__card-title">Map Scope</h3>
            <div className="geo-setup__scope-grid">
              {mapScopes.map((s) => (
                <button
                  key={s.id}
                  className={`geo-setup__scope ${selectedScope === s.id ? 'geo-setup__scope--active' : ''}`}
                  onClick={() => setSelectedScope(s.id)}
                >
                  <span className="geo-setup__scope-emoji">{s.emoji}</span>
                  <span className="geo-setup__scope-label">{s.label}</span>
                  <span className="geo-setup__scope-sub">{s.sub}</span>
                  {selectedScope === s.id && (
                    <span className="geo-setup__scope-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Bottom row: Cover Image + Round Settings */}
          <div className="geo-setup__bottom-row">
            {/* Cover Image + Title */}
            <div className="geo-setup__left-col">
              <div className="geo-setup__card">
                <label className="geo-setup__label">Game Title *</label>
                <input
                  className="geo-setup__input"
                  type="text"
                  placeholder="World Capitals Quiz"
                  value={gameTitle}
                  maxLength={80}
                  onChange={(e) => setGameTitle(e.target.value)}
                />
                <span className="geo-setup__char-count">
                  {gameTitle.length}/80
                </span>
              </div>
              <div className="geo-setup__card">
                <h4 className="geo-setup__card-title">Cover Image</h4>
                <div className="geo-setup__cover">
                  <FontAwesomeIcon
                    icon={faBullseye}
                    className="geo-setup__cover-icon"
                  />
                  <button className="geo-setup__cover-change">🖼 Change</button>
                </div>
              </div>
            </div>

            {/* Round Settings */}
            <div className="geo-setup__card">
              <h3 className="geo-setup__card-title">Round Settings</h3>

              <label className="geo-setup__label">Number of Rounds</label>
              <div className="geo-setup__counter">
                <button
                  className="geo-setup__counter-btn"
                  onClick={() => setRounds((r) => clamp(r - 1, 1, 20))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <span className="geo-setup__counter-value">{rounds}</span>
                <button
                  className="geo-setup__counter-btn"
                  onClick={() => setRounds((r) => clamp(r + 1, 1, 20))}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>

              <label className="geo-setup__label">Time per Round</label>
              <div className="geo-setup__counter">
                <button
                  className="geo-setup__counter-btn"
                  onClick={() => setTimePerRound((t) => clamp(t - 1, 1, 15))}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <span className="geo-setup__counter-value">
                  {timePerRound}mins
                </span>
                <button
                  className="geo-setup__counter-btn"
                  onClick={() => setTimePerRound((t) => clamp(t + 1, 1, 15))}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>

              <label className="geo-setup__label">Difficulty</label>
              <div className="geo-setup__pills">
                {difficulties.map((d) => (
                  <button
                    key={d}
                    className={`geo-setup__pill ${difficulty === d ? 'geo-setup__pill--active' : ''}`}
                    onClick={() => setDifficulty(d)}
                  >
                    {d}
                  </button>
                ))}
              </div>

              <div className="geo-setup__toggle-item">
                <FontAwesomeIcon
                  icon={faGamepad}
                  className="geo-setup__toggle-icon"
                />
                <span>Interactive Mode</span>
                <label className="geo-setup__toggle">
                  <input
                    type="checkbox"
                    checked={interactiveMode}
                    onChange={(e) => setInteractiveMode(e.target.checked)}
                  />
                  <span className="geo-setup__toggle-slider" />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel: Game Preview */}
        <div className="geo-setup__preview">
          <div className="geo-setup__card">
            <h3 className="geo-setup__card-title">Game Preview</h3>
            <div className="geo-setup__preview-map">
              <span className="geo-setup__preview-globe">🌍</span>
            </div>

            <div className="geo-setup__summary">
              <h4 className="geo-setup__summary-title">
                🗺️ Configuration Summary
              </h4>
              <div className="geo-setup__summary-row">
                <span>Map:</span>
                <span className="geo-setup__summary-val">{scopeLabel}</span>
              </div>
              <div className="geo-setup__summary-row">
                <span>Rounds:</span>
                <span className="geo-setup__summary-val">{rounds}</span>
              </div>
              <div className="geo-setup__summary-row">
                <span>Time Limit:</span>
                <span className="geo-setup__summary-val">
                  {timePerRound}mins
                </span>
              </div>
              <div className="geo-setup__summary-row">
                <span>Difficulty:</span>
                <span className="geo-setup__summary-badge">{difficulty}</span>
              </div>
            </div>

            <button className="geo-setup__publish-btn" onClick={handlePublish}>
              Publish
            </button>
          </div>
        </div>
      </div>

      {loading && <Loading message="Generating Geo Guess..." />}
      {alertMsg && (
        <Alert
          title="Notice"
          isOpen={!!alertMsg}
          cancelText="Ok"
          message={alertMsg}
          onCancel={() => setAlertMsg('')}
        />
      )}
    </div>
  );
};

export default GeoGuessSetup;
